/**
 * User API
 *
 * Mock user profile and settings management.
 */

import type {
  User,
  UserProfileWithStats,
  NotificationSettings,
  DeletionRequest,
  DataExportRequest,
  AccountLinkage,
  ApiResponse,
  UUID,
} from '../types'
import { mockDb, delay } from './mockDb'
import { success, error, isValidEmail } from './client'

// Session key (shared with auth.api.ts)
const SESSION_KEY = 'docurious-session'

/**
 * Get current user ID from session
 */
function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

/**
 * Simple privacy settings for UI
 */
interface SimplePrivacySettings {
  profileVisibility: 'public' | 'communities' | 'private'
  showActivityStatus: boolean
  allowFriendRequests: boolean
  showCompletedChallenges: boolean
  showSavedList: boolean
}

/**
 * Extended user record with settings
 */
interface UserRecord extends User {
  passwordHash: string
  notificationSettings?: NotificationSettings
  privacySettings?: SimplePrivacySettings
  level?: number
  xp?: number
  xpToNextLevel?: number
  totalChallengesCompleted?: number
  streakDays?: number
}

/**
 * Convert UserRecord to User (strip sensitive fields)
 */
function toPublicUser(record: UserRecord): User {
  const { passwordHash, ...user } = record
  return user
}

/**
 * User API
 */
export const userApi = {
  /**
   * Get current user profile with stats
   */
  async getProfile(): Promise<ApiResponse<UserProfileWithStats | null>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const userRecord = mockDb.findById<UserRecord>('users', userId)
    if (!userRecord) {
      return error('User not found')
    }

    const profile: UserProfileWithStats = {
      ...toPublicUser(userRecord),
      level: userRecord.level ?? 1,
      xp: userRecord.xp ?? 0,
      xpToNextLevel: userRecord.xpToNextLevel ?? 100,
      totalChallengesCompleted: userRecord.totalChallengesCompleted ?? 0,
      streakDays: userRecord.streakDays ?? 0,
      joinedAt: userRecord.createdAt,
    }

    return success(profile)
  },

  /**
   * Update user profile
   */
  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Validate email if provided
    if (data.email && !isValidEmail(data.email)) {
      return error('Invalid email format')
    }

    // Check for email uniqueness if changing
    if (data.email) {
      const existingUser = mockDb.findOne<UserRecord>(
        'users',
        (u) => u.email === data.email && u.id !== userId
      )
      if (existingUser) {
        return error('Email is already in use')
      }
    }

    const userRecord = mockDb.update<UserRecord>('users', userId, data)
    if (!userRecord) {
      return error('User not found')
    }

    return success(toPublicUser(userRecord))
  },

  /**
   * Get notification settings
   */
  async getNotificationSettings(): Promise<ApiResponse<NotificationSettings>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const userRecord = mockDb.findById<UserRecord>('users', userId)
    if (!userRecord) {
      return error('User not found')
    }

    // Return stored settings or defaults
    const settings: NotificationSettings = userRecord.notificationSettings ?? {
      emailNotifications: true,
      pushNotifications: true,
      challengeReminders: true,
      communityUpdates: true,
      marketingEmails: false,
      weeklyDigest: true,
    }

    return success(settings)
  },

  /**
   * Update notification settings
   */
  async updateNotificationSettings(
    settings: Partial<NotificationSettings>
  ): Promise<ApiResponse<NotificationSettings>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const userRecord = mockDb.findById<UserRecord>('users', userId)
    if (!userRecord) {
      return error('User not found')
    }

    const currentSettings: NotificationSettings = userRecord.notificationSettings ?? {
      emailNotifications: true,
      pushNotifications: true,
      challengeReminders: true,
      communityUpdates: true,
      marketingEmails: false,
      weeklyDigest: true,
    }

    const updatedSettings = { ...currentSettings, ...settings }

    mockDb.update<UserRecord>('users', userId, {
      notificationSettings: updatedSettings,
    })

    return success(updatedSettings)
  },

  /**
   * Get privacy settings
   */
  async getPrivacySettings(): Promise<ApiResponse<SimplePrivacySettings>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const userRecord = mockDb.findById<UserRecord>('users', userId)
    if (!userRecord) {
      return error('User not found')
    }

    const settings: SimplePrivacySettings = userRecord.privacySettings ?? {
      profileVisibility: 'public',
      showActivityStatus: true,
      allowFriendRequests: true,
      showCompletedChallenges: true,
      showSavedList: false,
    }

    return success(settings)
  },

  /**
   * Update privacy settings
   */
  async updatePrivacySettings(
    settings: Partial<SimplePrivacySettings>
  ): Promise<ApiResponse<SimplePrivacySettings>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const userRecord = mockDb.findById<UserRecord>('users', userId)
    if (!userRecord) {
      return error('User not found')
    }

    const currentSettings: SimplePrivacySettings = userRecord.privacySettings ?? {
      profileVisibility: 'public',
      showActivityStatus: true,
      allowFriendRequests: true,
      showCompletedChallenges: true,
      showSavedList: false,
    }

    const updatedSettings = { ...currentSettings, ...settings }

    mockDb.update<UserRecord>('users', userId, {
      privacySettings: updatedSettings,
    })

    return success(updatedSettings)
  },

  /**
   * Export user data (GDPR compliance)
   */
  async exportUserData(): Promise<ApiResponse<object>> {
    await delay(200)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const userRecord = mockDb.findById<UserRecord>('users', userId)
    if (!userRecord) {
      return error('User not found')
    }

    // Compile all user data
    const exportData = {
      profile: toPublicUser(userRecord),
      notificationSettings: userRecord.notificationSettings,
      privacySettings: userRecord.privacySettings,
      exportedAt: new Date().toISOString(),
    }

    return success(exportData)
  },

  /**
   * Request account deletion (30-day grace period; under-13 must use parent flow)
   */
  async requestDeletion(reason?: string): Promise<ApiResponse<DeletionRequest>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // COPPA: under-13 users cannot self-delete — parent must initiate via requestChildDeletion
    const userRecord = mockDb.findById<UserRecord>('users', userId)
    if (userRecord?.isUnder13) {
      return error('Under-13 account deletion must be requested by a parent or guardian')
    }

    // Check for existing deletion request
    const existingRequest = mockDb.findOne<DeletionRequest>(
      'deletionRequests' as any,
      (r) => r.userId === userId && r.status === 'pending'
    )
    if (existingRequest) {
      return error('Deletion request already pending')
    }

    const now = new Date()
    const scheduledDeletion = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000) // 30 days

    const deletionRequest: DeletionRequest = {
      id: `del-${Date.now()}`,
      userId,
      requestedAt: now.toISOString(),
      scheduledDeletionAt: scheduledDeletion.toISOString(),
      reason,
      status: 'pending',
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    }

    mockDb.insertWithId('deletionRequests' as any, deletionRequest)

    // Mark account as pending deletion
    mockDb.update<UserRecord>('users', userId, {
      accountStatus: 'pending_deletion',
    })

    return success(deletionRequest)
  },

  /**
   * Get current deletion request status
   */
  async getDeletionStatus(): Promise<ApiResponse<DeletionRequest | null>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const request = mockDb.findOne<DeletionRequest>(
      'deletionRequests' as any,
      (r) => r.userId === userId && r.status === 'pending'
    )

    return success(request)
  },

  /**
   * Cancel deletion request (within 30-day window)
   */
  async cancelDeletion(): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const request = mockDb.findOne<DeletionRequest>(
      'deletionRequests' as any,
      (r) => r.userId === userId && r.status === 'pending'
    )

    if (!request) {
      return error('No pending deletion request found')
    }

    // Update deletion request
    mockDb.update<DeletionRequest>('deletionRequests' as any, request.id, {
      status: 'cancelled',
      cancelledAt: new Date().toISOString(),
    })

    // Restore account status
    mockDb.update<UserRecord>('users', userId, {
      accountStatus: 'active',
    })

    return success(undefined)
  },

  /**
   * Legacy delete account (immediate - for backwards compat)
   */
  async deleteAccount(): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Mark account for deletion (30-day grace period in real app)
    const updated = mockDb.update<UserRecord>('users', userId, {
      accountStatus: 'pending_deletion',
    })

    if (!updated) {
      return error('User not found')
    }

    // Clear session
    localStorage.removeItem(SESSION_KEY)

    return success(undefined)
  },

  /**
   * Request data export (GDPR compliance)
   */
  async requestDataExport(options?: {
    format?: 'json' | 'csv'
    includeMedia?: boolean
  }): Promise<ApiResponse<DataExportRequest>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const now = new Date()
    const expiresAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000) // 7 days

    const exportRequest: DataExportRequest = {
      id: `export-${Date.now()}`,
      userId,
      requestedAt: now.toISOString(),
      status: 'processing',
      format: options?.format ?? 'json',
      includeMedia: options?.includeMedia ?? false,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    }

    mockDb.insertWithId('dataExports' as any, exportRequest)

    // Simulate async processing - complete immediately for mock
    setTimeout(async () => {
      const userRecord = mockDb.findById<UserRecord>('users', userId)
      if (!userRecord) return

      // Compile export data
      const exportData = {
        profile: toPublicUser(userRecord),
        settings: {
          notifications: userRecord.notificationSettings,
          privacy: userRecord.privacySettings,
        },
        challenges: mockDb.findWhere('userChallenges', (uc: any) => uc.userId === userId),
        trackRecords: mockDb.findWhere('trackRecords', (tr: any) => tr.userId === userId),
        communities: mockDb.findWhere('communityMemberships', (cm: any) => cm.userId === userId),
        exportedAt: new Date().toISOString(),
      }

      // Store export data as blob URL (in real app, this would be a file on storage)
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const downloadUrl = URL.createObjectURL(blob)

      mockDb.update<DataExportRequest>('dataExports' as any, exportRequest.id, {
        status: 'completed',
        downloadUrl,
        completedAt: new Date().toISOString(),
        expiresAt: expiresAt.toISOString(),
      })
    }, 500)

    return success(exportRequest)
  },

  /**
   * Get data export status
   */
  async getExportStatus(exportId: string): Promise<ApiResponse<DataExportRequest | null>> {
    await delay(50)

    const exportRequest = mockDb.findById<DataExportRequest>('dataExports' as any, exportId)
    return success(exportRequest)
  },

  /**
   * Get all user's export requests
   */
  async getExportHistory(): Promise<ApiResponse<DataExportRequest[]>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const exports = mockDb.findWhere<DataExportRequest>(
      'dataExports' as any,
      (e) => e.userId === userId
    )

    return success(exports.sort((a, b) =>
      new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
    ))
  },

  /**
   * Get user by ID (for public profiles)
   */
  async getUserById(userId: UUID): Promise<ApiResponse<User | null>> {
    await delay(50)

    const userRecord = mockDb.findById<UserRecord>('users', userId)
    if (!userRecord) {
      return success(null)
    }

    // Check privacy settings
    if (userRecord.privacySettings?.profileVisibility === 'private') {
      return success(null)
    }

    return success(toPublicUser(userRecord))
  },

  // ─── COPPA Compliance ──────────────────────────────────────────────

  /**
   * Request deletion of a linked child's account (parent-only, 48-hour COPPA window)
   */
  async requestChildDeletion(childUserId: UUID, reason?: string): Promise<ApiResponse<DeletionRequest>> {
    await delay(100)

    const parentId = getCurrentUserId()
    if (!parentId) return error('Not authenticated')

    const parent = mockDb.findById<UserRecord>('users', parentId)
    if (!parent || !parent.linkedChildIds?.includes(childUserId)) {
      return error('You are not linked to this child account')
    }

    const child = mockDb.findById<UserRecord>('users', childUserId)
    if (!child) return error('Child account not found')

    const existing = mockDb.findOne<DeletionRequest>(
      'deletionRequests' as any,
      (r) => r.userId === childUserId && r.status === 'pending'
    )
    if (existing) return error('Deletion request already pending for this child')

    const now = new Date()
    // COPPA: 48-hour deletion window for under-13 users
    const scheduledDeletion = new Date(now.getTime() + 48 * 60 * 60 * 1000)

    const deletionRequest: DeletionRequest = {
      id: `del-${Date.now()}`,
      userId: childUserId,
      requestedAt: now.toISOString(),
      scheduledDeletionAt: scheduledDeletion.toISOString(),
      reason,
      status: 'pending',
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    }

    mockDb.insertWithId('deletionRequests' as any, deletionRequest)
    mockDb.update<UserRecord>('users', childUserId, { accountStatus: 'pending_deletion' })

    return success(deletionRequest)
  },

  /**
   * Get linked children with their linkage status (for parent accounts)
   */
  async getLinkedChildren(): Promise<ApiResponse<{ child: User; linkage: AccountLinkage }[]>> {
    await delay(100)

    const parentId = getCurrentUserId()
    if (!parentId) return error('Not authenticated')

    const parent = mockDb.findById<UserRecord>('users', parentId)
    if (!parent?.linkedChildIds?.length) return success([])

    const results: { child: User; linkage: AccountLinkage }[] = []
    for (const childId of parent.linkedChildIds) {
      const child = mockDb.findById<UserRecord>('users', childId)
      if (!child) continue

      // Find or create mock linkage
      let linkage = mockDb.findOne<AccountLinkage>(
        'accountLinkages' as any,
        (l) => l.parentUserId === parentId && l.childUserId === childId
      )
      if (!linkage) {
        linkage = {
          id: `link-${parentId}-${childId}`,
          parentUserId: parentId,
          childUserId: childId,
          linkageType: 'parent_child',
          status: 'active',
          consentGranted: child.parentalConsentStatus === 'granted',
          consentGrantedAt: child.parentalConsentStatus === 'granted' ? child.updatedAt : undefined,
          reminderSentDay14: false,
          reminderSentDay28: false,
          createdAt: child.createdAt,
          updatedAt: child.updatedAt,
        }
        mockDb.insertWithId('accountLinkages' as any, linkage)
      }

      results.push({ child: toPublicUser(child), linkage })
    }

    return success(results)
  },

  /**
   * Grant parental consent for a linked child (upgrades Tier 1 → Tier 2)
   */
  async grantConsent(childUserId: UUID): Promise<ApiResponse<AccountLinkage>> {
    await delay(100)

    const parentId = getCurrentUserId()
    if (!parentId) return error('Not authenticated')

    const parent = mockDb.findById<UserRecord>('users', parentId)
    if (!parent?.linkedChildIds?.includes(childUserId)) {
      return error('You are not linked to this child account')
    }

    const now = new Date().toISOString()

    // Update the child's consent status and tier
    mockDb.update<UserRecord>('users', childUserId, {
      parentalConsentStatus: 'granted',
      accountTier: 'tier_2_full',
      updatedAt: now,
    })

    // Update or create the linkage record
    let linkage = mockDb.findOne<AccountLinkage>(
      'accountLinkages' as any,
      (l) => l.parentUserId === parentId && l.childUserId === childUserId
    )
    if (linkage) {
      linkage = mockDb.update<AccountLinkage>('accountLinkages' as any, linkage.id, {
        consentGranted: true,
        consentGrantedAt: now,
        consentRevokedAt: undefined,
        status: 'active',
        updatedAt: now,
      })!
    } else {
      linkage = {
        id: `link-${parentId}-${childUserId}`,
        parentUserId: parentId,
        childUserId: childUserId,
        linkageType: 'parent_child',
        status: 'active',
        consentGranted: true,
        consentGrantedAt: now,
        reminderSentDay14: false,
        reminderSentDay28: false,
        createdAt: now,
        updatedAt: now,
      }
      mockDb.insertWithId('accountLinkages' as any, linkage)
    }

    return success(linkage)
  },

  /**
   * Revoke parental consent for a linked child (downgrades Tier 2 → Tier 1)
   */
  async revokeConsent(childUserId: UUID): Promise<ApiResponse<AccountLinkage>> {
    await delay(100)

    const parentId = getCurrentUserId()
    if (!parentId) return error('Not authenticated')

    const parent = mockDb.findById<UserRecord>('users', parentId)
    if (!parent?.linkedChildIds?.includes(childUserId)) {
      return error('You are not linked to this child account')
    }

    const now = new Date().toISOString()

    // Downgrade child to Tier 1 and revoke consent
    mockDb.update<UserRecord>('users', childUserId, {
      parentalConsentStatus: 'revoked',
      accountTier: 'tier_1_school_only',
      updatedAt: now,
    })

    const linkage = mockDb.findOne<AccountLinkage>(
      'accountLinkages' as any,
      (l) => l.parentUserId === parentId && l.childUserId === childUserId
    )
    if (linkage) {
      const updated = mockDb.update<AccountLinkage>('accountLinkages' as any, linkage.id, {
        consentGranted: false,
        consentRevokedAt: now,
        updatedAt: now,
      })!
      return success(updated)
    }

    return error('Linkage not found')
  },

  /**
   * Initiate COPPA $1.00 payment verification for parental identity
   */
  async initiateCoppaPaymentVerification(childUserId: UUID): Promise<ApiResponse<{ verificationId: string; amount: number }>> {
    await delay(100)

    const parentId = getCurrentUserId()
    if (!parentId) return error('Not authenticated')

    const parent = mockDb.findById<UserRecord>('users', parentId)
    if (!parent?.linkedChildIds?.includes(childUserId)) {
      return error('You are not linked to this child account')
    }

    return success({
      verificationId: `coppa-verify-${Date.now()}`,
      amount: 1.00,
    })
  },

  /**
   * Complete COPPA payment verification — auto-grants consent on success
   */
  async completeCoppaPaymentVerification(
    verificationId: string,
    childUserId: UUID,
  ): Promise<ApiResponse<{ success: boolean }>> {
    await delay(1500) // Simulate payment processing

    // Mock: always succeeds — in production this would verify with Stripe
    const parentId = getCurrentUserId()
    if (!parentId) return error('Not authenticated')

    // Auto-grant consent after successful payment
    const now = new Date().toISOString()
    mockDb.update<UserRecord>('users', childUserId, {
      parentalConsentStatus: 'granted',
      accountTier: 'tier_2_full',
      updatedAt: now,
    })

    let linkage = mockDb.findOne<AccountLinkage>(
      'accountLinkages' as any,
      (l) => l.parentUserId === parentId && l.childUserId === childUserId
    )
    if (linkage) {
      mockDb.update<AccountLinkage>('accountLinkages' as any, linkage.id, {
        consentGranted: true,
        consentGrantedAt: now,
        status: 'active',
        updatedAt: now,
      })
    }

    return success({ success: true })
  },

  /**
   * Update per-field profile visibility settings
   */
  async updateProfileVisibility(fieldVisibility: Record<string, 'public' | 'friends' | 'private'>): Promise<ApiResponse<Record<string, 'public' | 'friends' | 'private'>>> {
    await delay(200)
    return success(fieldVisibility)
  },
}
